import {PortState} from '@colonist/game-state-types'
import {FogTileGroupStates, MapState, NestedReadonly, PlayerColor, TileEdgeDirection, TileEdgeType} from '@colonist/game-states'
import {ArrayUtils} from '@colonist/utils'
import {MapStateDepthFirstSearch} from './MapStateDepthFirstSearch'
import {HexEdge} from './Hexagon'
import {TileHex, TileCorner, isCornerBuildingKnight, TileEdge} from './Tile'
import {TileState} from './TileState'
import {MapPipStatistics} from '@colonist/game-base-types'

export class MapValidator {
    tileState: TileState
    portState: PortState
    fogTileGroupStates: FogTileGroupStates | undefined

    constructor(state: NestedReadonly<MapState>) {
        this.tileState = new TileState(state.tileHexStates, state.tileCornerStates, state.tileEdgeStates)
        this.portState = new PortState(state.portEdgeStates)
        this.fogTileGroupStates = state.fogTileGroupStates
    }

    isPortOnEastSideOfTile(tile: TileHex): boolean {
        const southEastEdge = new HexEdge(tile.hexFace.x, tile.hexFace.y + 1, TileEdgeDirection.NorthWest)
        if(this.portState.isPortOnEdge(southEastEdge)) return true

        const eastEdge = new HexEdge(tile.hexFace.x + 1, tile.hexFace.y, TileEdgeDirection.West)
        if(this.portState.isPortOnEdge(eastEdge)) return true

        const northEastEdge = new HexEdge(tile.hexFace.x + 1, tile.hexFace.y - 1, TileEdgeDirection.SouthWest)
        if(this.portState.isPortOnEdge(northEastEdge)) return true

        return false
    }

    isCornerKnightAndIsKnightNotTouchingEdgeType(player: PlayerColor, tileCorner: TileCorner, edgeType: TileEdgeType): boolean {
        if(!isCornerBuildingKnight(tileCorner.buildingType)) return false

        for(const touchingEdge of tileCorner.hexCorner.touchingEdges()) {
            const touchingTileEdge = this.tileState.getTileEdge(touchingEdge)
            if(touchingTileEdge == undefined) continue

            if(touchingTileEdge.type == undefined) continue
            if(touchingTileEdge.owner != player) continue

            if(touchingTileEdge.type == edgeType) return false
        }
        return true
    }

    areAdjacentCornersEmpty(tileCorner: TileCorner): boolean {
        for(const corner of tileCorner.hexCorner.adjacentCorners()) {
            const tileCornerOfAdjacentCorners = this.tileState.getTileCorner(corner)
            if(tileCornerOfAdjacentCorners == undefined) continue
            if(tileCornerOfAdjacentCorners.buildingType == undefined) continue
            if(isCornerBuildingKnight(tileCornerOfAdjacentCorners.buildingType)) continue

            return false
        }
        return true
    }

    areAdjacentEdgesEmpty(tileCorner: TileCorner): boolean {
        for(const edge of tileCorner.hexCorner.touchingEdges()) {
            const tileEdge = this.tileState.getTileEdge(edge)
            if(tileEdge == undefined) continue

            if(tileEdge.type != undefined) return false
        }
        return true
    }

    generateEdgeMapForPlayerAndPieces(player: PlayerColor, edgeTypes: TileEdgeType[]): TileEdge[] {
        const edgeMap: TileEdge[] = []
        for(const tileEdge of this.tileState.tileEdges) {
            if(tileEdge.owner != player) continue
            if(tileEdge.type == undefined) continue
            if(!edgeTypes.includes(tileEdge.type)) continue
            edgeMap.push(tileEdge)
        }
        return edgeMap
    }

    willMovingEdgeTypeBreakClosedRoute(player: PlayerColor, tileEdge: TileEdge, edgeType: TileEdgeType): boolean {
        const edgeMap = this.generateEdgeMapForPlayerAndPieces(player, [edgeType])

        const ignoreOpponentBuildings = true
        const ignoreMyBuildings = false
        const mapDepthFirstSearch = new MapStateDepthFirstSearch(player, edgeMap, tileEdge, this, ignoreOpponentBuildings, ignoreMyBuildings)

        if(this.isEdgeTypeAtTheEndOfARoute(tileEdge, edgeType)) return false

        const uniqueInterConnectedCornerBuildings = ArrayUtils.removeDuplicatesFromArray([...mapDepthFirstSearch.interConnectedCornerBuildings])
        const isEdgeTouching2OrMoreBuildings = uniqueInterConnectedCornerBuildings.length >= 2
        if(isEdgeTouching2OrMoreBuildings) return true

        // edge cases from: https://www.catan.com/faq/4792-ships-when-ship-open
        if(this.edgeCaseIsEdgePartOfLoopAndTouchingSingleBuilding(tileEdge, mapDepthFirstSearch.interConnectedCornerBuildings)) return false
        if(this.edgeCaseIsEdgePartOfLoopNotTouchingBuilding(player, tileEdge, edgeMap, edgeType)) return false

        return true
    }

    private edgeCaseIsEdgePartOfLoopAndTouchingSingleBuilding(tileEdge: TileEdge, interConnectedCornerBuildings: number[]): boolean {
        const edgeCaseRequiresAtLeast2TouchingBuildings = 2
        if(interConnectedCornerBuildings.length < edgeCaseRequiresAtLeast2TouchingBuildings) return false

        const uniqueInterConnectedCornerBuildings = ArrayUtils.removeDuplicatesFromArray(interConnectedCornerBuildings)
        const edgeCaseRequiresTouchingBuildingToBeIdentical = uniqueInterConnectedCornerBuildings.length == 1
        if(!edgeCaseRequiresTouchingBuildingToBeIdentical) return false

        const touchingCornerBuildingOnLoopIndex = uniqueInterConnectedCornerBuildings[0]
        const touchingCornerBuildingOnLoop = this.tileState.tileCorners[touchingCornerBuildingOnLoopIndex]

        const isTouchingBuildingOwnedByPlayer = tileEdge.owner == touchingCornerBuildingOnLoop.owner
        if(!isTouchingBuildingOwnedByPlayer) return false

        for(const endPoints of tileEdge.hexEdge.endPoints()) {
            if(endPoints.sameCorner(touchingCornerBuildingOnLoop.hexCorner)) return true
        }
        return false
    }

    private edgeCaseIsEdgePartOfLoopNotTouchingBuilding(player: PlayerColor, tileEdge: TileEdge, edgeMap: TileEdge[], edgeType: TileEdgeType): boolean {
        ArrayUtils.removeElementFromArray(edgeMap, tileEdge)
        const hypotheticalEdgeMapWithEdgeRemoved = edgeMap
        const ignoreOpponentBuildings = true
        const ignoreMyBuildings = false

        for(const corner of tileEdge.hexEdge.endPoints()) {
            const tileCorner = this.tileState.getTileCorner(corner)
            if(tileCorner == undefined) continue

            for(const touchingEdge of corner.touchingEdges()) {
                const touchingTileEdge = this.tileState.getTileEdge(touchingEdge)
                if(touchingTileEdge == undefined) continue
                if(touchingTileEdge.sameEdge(tileEdge)) continue

                if(touchingTileEdge.owner != tileEdge.owner) continue
                if(touchingTileEdge.type != edgeType) continue

                const mapDepthFirstSearchWithEdgeRemoved = new MapStateDepthFirstSearch(player, hypotheticalEdgeMapWithEdgeRemoved, touchingTileEdge, this, ignoreOpponentBuildings, ignoreMyBuildings)

                if(this.edgeCaseIsTouchingEdgeLoopingBackToOtherEndOfEdge(tileEdge, mapDepthFirstSearchWithEdgeRemoved.interConnectedEmptyNodes)) return true
            }
        }
        return false
    }

    private edgeCaseIsTouchingEdgeLoopingBackToOtherEndOfEdge(tileEdge: TileEdge, interConnectedEmptyNodes: number[]): boolean {
        const endPointIndexes: number[] = []
        for(const corner of tileEdge.hexEdge.endPoints()) {
            const tileCorner = this.tileState.getTileCorner(corner)
            if(tileCorner == undefined) continue

            const cornerIndex = this.tileState.getTileCornerIndex(tileCorner.hexCorner)
            if(cornerIndex == undefined) continue

            endPointIndexes.push(cornerIndex)
        }
        for(const edgeEndPoint of endPointIndexes) {
            if(!interConnectedEmptyNodes.includes(edgeEndPoint)) return false
        }
        return true
    }

    private isEdgeTypeAtTheEndOfARoute(tileEdge: TileEdge, edgeType: TileEdgeType): boolean {
        const cornersConnectingARoute: TileCorner[] = []
        for(const corner of tileEdge.hexEdge.endPoints()) {
            const tileCorner = this.tileState.getTileCorner(corner)
            if(tileCorner == undefined) continue
            if(tileCorner.owner == tileEdge.owner) {
                cornersConnectingARoute.push(tileCorner)
                continue
            }

            for(const touchingEdges of corner.touchingEdges()) {
                const touchingTileEdges = this.tileState.getTileEdge(touchingEdges)
                if(touchingTileEdges == undefined) continue
                if(touchingTileEdges.sameEdge(tileEdge)) continue

                if(touchingTileEdges.owner != tileEdge.owner) continue
                if(touchingTileEdges.type != edgeType) continue
                if(cornersConnectingARoute.includes(tileCorner)) continue
                cornersConnectingARoute.push(tileCorner)
            }
        }
        if(cornersConnectingARoute.length < 2) return true
        return false
    }

    arePlayersProtectedByFriendlyRobberTouchingTile(playersProtectedByFriendlyRobber: PlayerColor[], tileIndex: number): boolean {
        if(playersProtectedByFriendlyRobber.length == 0) return false

        const playersTouchingTile = this.playersWithBuildingsAroundTile(tileIndex)
        for(const player of playersTouchingTile) {
            if(playersProtectedByFriendlyRobber.includes(player)) return true
        }
        return false
    }

    doPlayersProtectedByFriendlyRobberHaveShipTouchingTile(playersProtectedByFriendlyRobber: PlayerColor[], tileIndex: number): boolean {
        if(playersProtectedByFriendlyRobber.length == 0) return false

        const playersWithShipsTouchingTile = this.playersWithEdgePieceAroundTile(tileIndex, TileEdgeType.Ship)
        for(const player of playersWithShipsTouchingTile) {
            if(playersProtectedByFriendlyRobber.includes(player)) return true
        }
        return false
    }

    playersWithBuildingsAroundTile(tileNo: number): PlayerColor[] {
        const colors: PlayerColor[] = []

        for(const corner of this.tileState.tiles[tileNo].hexFace.corners()) {
            const tileCorner = this.tileState.getTileCorner(corner)
            if(tileCorner == undefined) continue
            if(tileCorner.owner == undefined) continue
            if(isCornerBuildingKnight(tileCorner.buildingType)) continue
            if(colors.includes(tileCorner.owner)) continue
            colors.push(tileCorner.owner)
        }
        return colors
    }

    playersWithEdgePieceAroundTile(tileNo: number, edgeType: TileEdgeType): PlayerColor[] {
        const colors: PlayerColor[] = []
        for(const edge of this.tileState.tiles[tileNo].hexFace.edges()) {
            const tileEdge = this.tileState.getTileEdge(edge)
            if(tileEdge == undefined) continue
            if(tileEdge.owner == undefined) continue
            if(tileEdge.type != edgeType) continue
            if(colors.includes(tileEdge.owner)) continue

            colors.push(tileEdge.owner)
        }
        return colors
    }

    getMapPipStatistics(): MapPipStatistics {
        const mapPipStatistics: MapPipStatistics = {}

        for(const tile of this.tileState.tiles) {
            const totalTilePipCount = mapPipStatistics[tile.tileType] ?? 0
            mapPipStatistics[tile.tileType] = totalTilePipCount + tile.diceProbability
        }

        return mapPipStatistics
    }

    getLongestRouteTouchingTileEdge(tileEdge: TileEdge): TileEdge[] {
        if(tileEdge.owner == undefined) return []

        const tileEdgeIndex = this.tileState.getTileEdgeIndex(tileEdge.hexEdge)
        if(tileEdgeIndex == undefined) return []

        const ignoreOpponentBuildings = false
        const ignoreMyBuildings = true
        const edgeMap = this.generateEdgeMapForPlayerAndPieces(tileEdge.owner, [TileEdgeType.Road, TileEdgeType.Ship])
        const mapSearchForConnectedEdges = new MapStateDepthFirstSearch(tileEdge.owner, edgeMap, tileEdge, this, ignoreOpponentBuildings, ignoreMyBuildings)

        const connectedEdges = mapSearchForConnectedEdges.allConnectedTileEdges

        let longestRouteTouchingTile: TileEdge[] = []
        for(const startingEdge of connectedEdges) {
            const mapSearchForLongestRoad: MapStateDepthFirstSearch = new MapStateDepthFirstSearch(tileEdge.owner, connectedEdges, startingEdge, this, ignoreOpponentBuildings, ignoreMyBuildings)

            const doesLongestRoadContainEdge = mapSearchForLongestRoad.longestConnectedRoadEdges.includes(tileEdge)
            if(doesLongestRoadContainEdge == false) continue

            if(longestRouteTouchingTile.length >= mapSearchForLongestRoad.longestConnectedRoadEdges.length) continue

            longestRouteTouchingTile = [...mapSearchForLongestRoad.longestConnectedRoadEdges]
        }

        return longestRouteTouchingTile
    }
}
